import colors from 'src/theme/colors';
import { ProductPassportData } from '../types';

export type PassportBlockType =
  | 'hero'
  | 'about-dpp-lca'
  | 'product-identification'
  | 'environmental-footprint'
  | 'material-composition'
  | 'durability-repairability'
  | 'recycling-end-of-life'
  | 'regulatory-compliance'
  | 'product-durability-reliability'
  | 'certifications'
  | 'carbon-footprint'
  | 'life-cycle-impact-details'
  | 'qr-share'
  | 'custom-text';

export type PassportBlockVariant = 'table' | 'cards';
export type PassportBlockWidth = 'full' | 'half';
export type PassportBlockDataStatus = 'ready' | 'partial' | 'missing';

export interface PassportBrandingSettings {
  accentColor: string;
  logoUrl: string | null;
}

export interface PassportLayoutBlock {
  id: string;
  type: PassportBlockType;
  enabled: boolean;
  required: boolean;
  titleOverride?: string;
  variant?: PassportBlockVariant;
  width?: PassportBlockWidth;
  content?: string;
}

export interface PassportBlockDefinition {
  type: PassportBlockType;
  titleKey: string;
  descriptionKey: string;
  required: boolean;
  allowMultiple: boolean;
  supportsVariant: boolean;
  supportsWidth: boolean;
  defaultVariant?: PassportBlockVariant;
  defaultWidth?: PassportBlockWidth;
}

const PASSPORT_LAYOUT_STORAGE_PREFIX = 'lca-passport-layout';
const PASSPORT_BRANDING_STORAGE_PREFIX = 'lca-passport-branding';
const ADDITIONAL_ENVIRONMENTAL_FIELDS: Array<keyof ProductPassportData> = [
  'product_carbon_footprint',
  'emissions_to_air_water_soil_noise',
  'microplastics_nanoplastics_release',
  'energy_water_resource_consumption',
  'primary_energy_use',
  'recovered_exported_energy',
  'product_performance_factors_impact',
  'deforestation_impact',
  'recycled_materials_share',
  'material_recovery_critical_raw_materials',
  'renewable_materials_content',
  'waste_generated',
  'co2_sequestration_potential',
  'ozone_depletion_potential',
  'acidification_potential',
  'eutrophication_potential',
  'photochemical_ozone_creation_potential',
];

export const PASSPORT_BLOCK_DEFINITIONS: Record<
  PassportBlockType,
  PassportBlockDefinition
> = {
  hero: {
    type: 'hero',
    titleKey: 'hero',
    descriptionKey: 'builder.blockDescriptions.hero',
    required: true,
    allowMultiple: false,
    supportsVariant: false,
    supportsWidth: false,
    defaultWidth: 'full',
  },
  'about-dpp-lca': {
    type: 'about-dpp-lca',
    titleKey: 'aboutDppLca',
    descriptionKey: 'builder.blockDescriptions.aboutDppLca',
    required: false,
    allowMultiple: false,
    supportsVariant: false,
    supportsWidth: true,
    defaultWidth: 'full',
  },
  'product-identification': {
    type: 'product-identification',
    titleKey: 'productIdentification',
    descriptionKey: 'builder.blockDescriptions.productIdentification',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  'environmental-footprint': {
    type: 'environmental-footprint',
    titleKey: 'environmentalFootprint',
    descriptionKey: 'builder.blockDescriptions.environmentalFootprint',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  'material-composition': {
    type: 'material-composition',
    titleKey: 'materialComposition',
    descriptionKey: 'builder.blockDescriptions.materialComposition',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'full',
  },
  'durability-repairability': {
    type: 'durability-repairability',
    titleKey: 'durabilityRepairability',
    descriptionKey: 'builder.blockDescriptions.durabilityRepairability',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  'recycling-end-of-life': {
    type: 'recycling-end-of-life',
    titleKey: 'recyclingEndOfLife',
    descriptionKey: 'builder.blockDescriptions.recyclingEndOfLife',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  'regulatory-compliance': {
    type: 'regulatory-compliance',
    titleKey: 'regulatoryCompliance',
    descriptionKey: 'builder.blockDescriptions.regulatoryCompliance',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  'product-durability-reliability': {
    type: 'product-durability-reliability',
    titleKey: 'productDurabilityReliability',
    descriptionKey: 'builder.blockDescriptions.productDurabilityReliability',
    required: false,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'half',
  },
  certifications: {
    type: 'certifications',
    titleKey: 'certifications',
    descriptionKey: 'builder.blockDescriptions.certifications',
    required: false,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'cards',
    defaultWidth: 'half',
  },
  'carbon-footprint': {
    type: 'carbon-footprint',
    titleKey: 'carbonFootprint',
    descriptionKey: 'builder.blockDescriptions.carbonFootprint',
    required: true,
    allowMultiple: false,
    supportsVariant: false,
    supportsWidth: false,
    defaultWidth: 'full',
  },
  'life-cycle-impact-details': {
    type: 'life-cycle-impact-details',
    titleKey: 'lifeCycleImpactDetails',
    descriptionKey: 'builder.blockDescriptions.lifeCycleImpactDetails',
    required: true,
    allowMultiple: false,
    supportsVariant: true,
    supportsWidth: true,
    defaultVariant: 'table',
    defaultWidth: 'full',
  },
  'qr-share': {
    type: 'qr-share',
    titleKey: 'qrShare',
    descriptionKey: 'builder.blockDescriptions.qrShare',
    required: false,
    allowMultiple: false,
    supportsVariant: false,
    supportsWidth: true,
    defaultWidth: 'half',
  },
  'custom-text': {
    type: 'custom-text',
    titleKey: 'customText',
    descriptionKey: 'builder.blockDescriptions.customText',
    required: false,
    allowMultiple: true,
    supportsVariant: false,
    supportsWidth: true,
    defaultWidth: 'half',
  },
};

export const PASSPORT_DEFAULT_BLOCKS: PassportBlockType[] = [
  'hero',
  'about-dpp-lca',
  'product-identification',
  'environmental-footprint',
  'material-composition',
  'durability-repairability',
  'recycling-end-of-life',
  'regulatory-compliance',
  'product-durability-reliability',
  'certifications',
  'carbon-footprint',
  'life-cycle-impact-details',
  'qr-share',
];

const getStorageKey = (passportToken: string) =>
  `${PASSPORT_LAYOUT_STORAGE_PREFIX}:${passportToken}`;

const getBrandingStorageKey = (passportToken: string) =>
  `${PASSPORT_BRANDING_STORAGE_PREFIX}:${passportToken}`;

const isValidHexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);
const hasPassportValue = (
  value: string | number | boolean | null | undefined
) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return value !== null && value !== undefined;
};

const createBlockId = (type: PassportBlockType) =>
  `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createPassportBlock = (
  type: PassportBlockType
): PassportLayoutBlock => {
  const definition = PASSPORT_BLOCK_DEFINITIONS[type];

  return {
    id: createBlockId(type),
    type,
    enabled: true,
    required: definition.required,
    variant: definition.defaultVariant,
    width: definition.defaultWidth,
    content:
      type === 'custom-text'
        ? 'Add your own narrative, support details, or handling instructions.'
        : undefined,
  };
};

export const createDefaultPassportBlocks = (): PassportLayoutBlock[] =>
  PASSPORT_DEFAULT_BLOCKS.map((type) => createPassportBlock(type));

const normalizePassportBlocks = (
  blocks: PassportLayoutBlock[] | null | undefined
): PassportLayoutBlock[] => {
  const sourceBlocks =
    blocks && blocks.length ? blocks : createDefaultPassportBlocks();
  const normalized: PassportLayoutBlock[] = [];

  sourceBlocks.forEach((block) => {
    const definition = PASSPORT_BLOCK_DEFINITIONS[block.type];
    if (!definition) return;
    if (
      !definition.allowMultiple &&
      normalized.some((item) => item.type === block.type)
    ) {
      return;
    }
    normalized.push({
      ...block,
      required: definition.required,
      enabled: block.enabled ?? true,
      variant: definition.supportsVariant
        ? block.variant || definition.defaultVariant
        : undefined,
      width: definition.supportsWidth
        ? block.width || definition.defaultWidth
        : definition.defaultWidth,
    });
  });

  PASSPORT_DEFAULT_BLOCKS.forEach((type) => {
    const definition = PASSPORT_BLOCK_DEFINITIONS[type];
    if (!definition.required) return;
    if (!normalized.some((block) => block.type === type)) {
      normalized.push(createPassportBlock(type));
    }
  });

  return normalized;
};

export const getSavedPassportBlocks = (
  passportToken: string
): PassportLayoutBlock[] | null => {
  const raw = localStorage.getItem(getStorageKey(passportToken));
  if (!raw) return null;

  try {
    return normalizePassportBlocks(JSON.parse(raw) as PassportLayoutBlock[]);
  } catch {
    return null;
  }
};

export const getResolvedPassportBlocks = (
  passportToken: string
): PassportLayoutBlock[] =>
  getSavedPassportBlocks(passportToken) || createDefaultPassportBlocks();

export const savePassportBlocks = (
  passportToken: string,
  blocks: PassportLayoutBlock[]
) => {
  localStorage.setItem(
    getStorageKey(passportToken),
    JSON.stringify(normalizePassportBlocks(blocks))
  );
};

export const resetPassportBlocks = (passportToken: string) => {
  localStorage.removeItem(getStorageKey(passportToken));
};

export const createDefaultPassportBranding = (): PassportBrandingSettings => ({
  accentColor: colors.lcaMainDark,
  logoUrl: null,
});

const normalizePassportBranding = (
  branding: PassportBrandingSettings | null | undefined
): PassportBrandingSettings => {
  const defaults = createDefaultPassportBranding();
  return {
    accentColor:
      branding?.accentColor && isValidHexColor(branding.accentColor)
        ? branding.accentColor
        : defaults.accentColor,
    logoUrl: branding?.logoUrl || null,
  };
};

export const getSavedPassportBranding = (
  passportToken: string
): PassportBrandingSettings | null => {
  const raw = localStorage.getItem(getBrandingStorageKey(passportToken));
  if (!raw) return null;

  try {
    return normalizePassportBranding(
      JSON.parse(raw) as PassportBrandingSettings
    );
  } catch {
    return null;
  }
};

export const getResolvedPassportBranding = (
  passportToken: string
): PassportBrandingSettings =>
  getSavedPassportBranding(passportToken) || createDefaultPassportBranding();

export const savePassportBranding = (
  passportToken: string,
  branding: PassportBrandingSettings
) => {
  localStorage.setItem(
    getBrandingStorageKey(passportToken),
    JSON.stringify(normalizePassportBranding(branding))
  );
};

export const resetPassportBranding = (passportToken: string) => {
  localStorage.removeItem(getBrandingStorageKey(passportToken));
};

export const canAddPassportBlock = (
  blocks: PassportLayoutBlock[],
  type: PassportBlockType
) => {
  const definition = PASSPORT_BLOCK_DEFINITIONS[type];
  if (definition.allowMultiple) return true;
  return !blocks.some((block) => block.type === type);
};

type PassportDataStatusOptions = {
  impactsLoading?: boolean;
};

export const getPassportMissingFields = (
  product: ProductPassportData,
  options: PassportDataStatusOptions = {}
) => {
  const checks = [
    {
      id: 'name',
      labelKey: 'productName',
      isMissing: !product.name,
    },
    {
      id: 'manufacturer_name',
      labelKey: 'manufacturerName',
      isMissing: !product.manufacturer_name,
    },
    {
      id: 'model_batch_serial_number',
      labelKey: 'modelBatchSerialNumber',
      isMissing: !product.model_batch_serial_number,
    },
    {
      id: 'production_country',
      labelKey: 'countryOfManufacture',
      isMissing: !product.production_country?.name,
    },
    {
      id: 'materials',
      labelKey: 'materialComposition',
      isMissing: !product.materials.length,
    },
    {
      id: 'repair_disassembly_instructions_description',
      labelKey: 'repairDisassemblyInstructions',
      isMissing: !product.repair_disassembly_instructions_description,
    },
    {
      id: 'recycling_possibilities',
      labelKey: 'recyclability',
      isMissing: !product.recycling_possibilities,
    },
    {
      id: 'impacts',
      labelKey: 'carbonFootprint',
      isMissing: !options.impactsLoading && !product.impacts.length,
    },
  ];

  return checks.filter((check) => check.isMissing);
};

const getBlockChecks = (
  product: ProductPassportData,
  type: PassportBlockType,
  options: PassportDataStatusOptions = {}
) => {
  const hasAdditionalEnvironmentalData = ADDITIONAL_ENVIRONMENTAL_FIELDS.some(
    (field) => {
      const value = product[field];
      return typeof value === 'string' && value.trim().length > 0;
    }
  );

  switch (type) {
    case 'hero':
      return [
        hasPassportValue(product.name),
        hasPassportValue(product.description),
        hasPassportValue(product.manufacturer_name),
        hasPassportValue(product.production_country?.name),
        hasPassportValue(product.weight) &&
          hasPassportValue(product.weight_unit),
        hasPassportValue(product.image_url),
      ];
    case 'product-identification':
      return [
        hasPassportValue(product.name),
        hasPassportValue(product.manufacturer_name),
        hasPassportValue(product.importer_name),
        hasPassportValue(product.model_batch_serial_number),
        hasPassportValue(product.gtin_code),
        hasPassportValue(product.production_country?.name),
      ];
    case 'about-dpp-lca':
      return [true];
    case 'environmental-footprint':
      return [
        hasPassportValue(product.weight) &&
          hasPassportValue(product.weight_unit),
        hasPassportValue(product.data_collection_period),
        hasPassportValue(product.proportion_recycled_materials),
      ];
    case 'material-composition':
      return [
        hasPassportValue(product.basic_material),
        product.materials.length > 0,
      ];
    case 'durability-repairability':
      return [
        hasPassportValue(
          product.expected_product_lifetime_years ||
            product.reference_service_life
        ),
        hasPassportValue(product.repairability_information),
        hasPassportValue(product.availability_of_spare_parts),
        hasPassportValue(
          product.compatibility_with_commonly_available_tools_and_spare_parts
        ),
        hasPassportValue(product.repair_disassembly_instructions_description),
        hasPassportValue(product.proper_use_instructions),
      ];
    case 'recycling-end-of-life':
      return [
        hasPassportValue(product.recycling_possibilities),
        hasPassportValue(product.disassembly_instructions),
        hasPassportValue(product.epr_information_description),
      ];
    case 'regulatory-compliance':
      return [product.ce_mark !== null && product.ce_mark !== undefined];
    case 'product-durability-reliability':
      return [
        hasPassportValue(product.guaranteed_service_life),
        hasPassportValue(product.operating_life),
        hasPassportValue(product.mean_time_between_failures),
        hasPassportValue(product.actual_product_use_information),
        hasPassportValue(product.resistance_to_stress_or_aging_mechanisms),
      ];
    case 'certifications':
      return [product.certificates.length > 0];
    case 'carbon-footprint':
      return [options.impactsLoading || product.impacts.length > 0];
    case 'life-cycle-impact-details':
      return [
        options.impactsLoading ||
          product.impacts.length > 0 ||
          hasAdditionalEnvironmentalData,
        product.diagram.stages.length > 0 || hasAdditionalEnvironmentalData,
      ];
    case 'qr-share':
      return [true];
    case 'custom-text':
      return [true];
    default:
      return [];
  }
};

export const getPassportBlockDataStatus = (
  product: ProductPassportData,
  type: PassportBlockType,
  options: PassportDataStatusOptions = {}
) => {
  const checks = getBlockChecks(product, type, options);
  const filled = checks.filter(Boolean).length;
  const total = checks.length;
  let status: PassportBlockDataStatus = 'missing';

  if (filled === total) {
    status = 'ready';
  } else if (filled > 0) {
    status = 'partial';
  }

  return {
    status,
    filled,
    total,
    missing: total - filled,
  };
};
